import React, { useState, useEffect, useRef } from 'react';
import {
  WizardShell,
  StepDefinition,
  PaymentUnsuccessfulScreen
} from 'components/PremiumWizard';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { uniq } from 'lodash';
import { Loading } from 'carbon-components-react';
import { StripeEmbeddedCheckout } from 'components';
import TransactionTypeStep, {
  AuthorisationType
} from './components/TransactionTypeStep';
import TransactionAmountStep, {
  MIN_TRANSACTION_QUANTITY
} from './components/TransactionAmountStep';
import AuthoriseStep, {
  AuthoriseStepValue,
  isAuthoriseStepValid
} from './components/AuthoriseStep';
import InvoicePayment from './components/InvoicePayment';
import CompletedStep from './components/CompletedStep';
import {
  listAuthorisationsStart,
  purchaseAuthorisationStart,
  purchaseAuthorisationOrderStart,
  clearPurchaseAuthorisation,
  clearError,
  clearAllAuthorisations
} from 'redux/authorisation/actions';
import {
  clearPaymentState,
  getPaymentTypePriceStart,
  confirmPostPaymentStart,
  checkCheckoutSessionStatusStart
} from 'redux/payment/actions';
import {
  selectGettingPaymentTypePrice,
  selectPaymentTypePrice,
  selectPriceIncludeTax,
  selectIsCheckingCheckoutSessionStatus,
  selectCheckoutSessionStatus,
  selectPaymentIntentId,
  selectConfirmingPostPayment,
  selectPostPaymentConfirmed,
  selectPostPaymentId,
  selectPaymentError
} from 'redux/payment/selectors';
import {
  selectAuthorisations,
  selectAuthorisationProcess,
  selectAuthorisationOrderProcess,
  selectAuthorisationError,
  selectAuthorisationPurchaseId
} from 'redux/authorisation/selectors';
import { setPurchasedAuthorisation } from 'redux/session/actions';
import { selectPurchasedAuthorisation } from 'redux/session/selectors';
import { APP_URL, Params } from 'types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getNewDate } from 'utilities/helper.util';

const steps: StepDefinition[] = [
  { id: 'transactionType', label: 'Transaction type' },
  { id: 'totalCount', label: 'Transactions & pricing' },
  { id: 'authorise', label: 'Authorise' },
  { id: 'payment', label: 'Payment' },
  { id: 'complete', label: 'Complete' }
];

// Wider spacing than default 20/40/60/80 to avoid label overlap on long step names.
const PURCHASE_TRANSACTION_STEP_POSITIONS = [31, 50, 70, 90];

const PurchaseTransactions: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const authorisations = useSelector(selectAuthorisations);
  const authorisationProcess = useSelector(selectAuthorisationProcess);
  const authorisationOrderProcess = useSelector(
    selectAuthorisationOrderProcess
  );
  const authorisationError = useSelector(selectAuthorisationError);
  const gettingPaymentTypePrice = useSelector(selectGettingPaymentTypePrice);
  const paymentTypePrice = useSelector(selectPaymentTypePrice);
  const priceIncludeTax = useSelector(selectPriceIncludeTax);
  const authorisationPurchaseId = useSelector(selectAuthorisationPurchaseId);
  const paymentIntentId = useSelector(selectPaymentIntentId);
  const isCheckingCheckoutSessionStatus = useSelector(
    selectIsCheckingCheckoutSessionStatus
  );
  const checkoutSessionStatus = useSelector(selectCheckoutSessionStatus);
  const postPaymentId = useSelector(selectPostPaymentId);
  const purchasedAuthorisation = useSelector(selectPurchasedAuthorisation);
  const confirmingPostPayment = useSelector(selectConfirmingPostPayment);
  const postPaymentConfirmed = useSelector(selectPostPaymentConfirmed);
  const paymentError = useSelector(selectPaymentError);
  const listAuthorisationsStartedRef = useRef<boolean>(false);
  const [
    isListAuthorisationsLoading,
    setIsListAuthorisationsLoading
  ] = useState<boolean>(true);
  const [activeStep, setActiveStep] = useState(0);
  const [authorisationType, setAuthorisationType] = useState<AuthorisationType>(
    'CREATE_ENQUIRY'
  );
  const [expiredAt, setExpiredAt] = useState<Date | undefined>(undefined);
  const [transactionQuantity, setTransactionQuantity] = useState(
    MIN_TRANSACTION_QUANTITY
  );
  const [previousBalance, setPreviousBalance] = useState<number>(0);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState<string>('');
  const [authoriseValue, setAuthoriseValue] = useState<AuthoriseStepValue>({
    mode: 'add-specific-users',
    existingEmails: [],
    existingDomains: [],
    emails: '',
    domains: '',
    isExisting: false,
    confirmed: false
  });
  const hidePrimaryAction =
    (activeStep === 3 && !postPaymentId) ||
    activeStep >= 4 ||
    isCheckingCheckoutSessionStatus ||
    confirmingPostPayment ||
    authorisationProcess === 'purchasing' ||
    authorisationOrderProcess === 'purchasing' ||
    checkoutSessionStatus === 'FAILED' ||
    paymentError;
  const showBack = !!(
    activeStep > 0 &&
    (activeStep <= 2 || (activeStep === 3 && postPaymentId)) &&
    !isCheckingCheckoutSessionStatus &&
    !confirmingPostPayment &&
    authorisationProcess !== 'purchasing' &&
    authorisationOrderProcess !== 'purchasing' &&
    checkoutSessionStatus !== 'FAILED' &&
    !paymentError
  );
  const goBack = () => {
    setActiveStep(activeStep - 1);
    if (activeStep === 3 && postPaymentId) {
      dispatch(clearPurchaseAuthorisation());
      dispatch(clearPaymentState());
      dispatch(getPaymentTypePriceStart(authorisationType));
      setPurchaseOrderNumber('');
    }
  };

  const isContinueDisabled = (): boolean => {
    if (gettingPaymentTypePrice) return true;
    if (activeStep === 0) return isListAuthorisationsLoading;
    if (activeStep === 1) return transactionQuantity <= 0;
    if (activeStep === 2) return !isAuthoriseStepValid(authoriseValue);
    return false;
  };

  const handlePrimaryActionClick = () => {
    if (activeStep === 0) {
      dispatch(getPaymentTypePriceStart(authorisationType));
      setActiveStep(activeStep + 1);
    } else if (activeStep === 2) {
      const datePlusOneYear = new Date();
      datePlusOneYear.setFullYear(datePlusOneYear.getFullYear() + 1);
      setExpiredAt(datePlusOneYear);
      const existingAuthorisation = authorisations?.find(
        (authorisation) => authorisation.authorisationType === authorisationType
      );
      if (existingAuthorisation?.id) {
        const orderData = {
          id: existingAuthorisation.id,
          payload: {
            users:
              authoriseValue.mode === 'add-specific-users'
                ? uniq([
                    ...authoriseValue.existingEmails,
                    ...(authoriseValue.emails
                      ? authoriseValue.emails.split(',').map((s) => s.trim())
                      : [])
                  ])
                : undefined,
            domains:
              authoriseValue.mode === 'match-email-domains'
                ? uniq([
                    ...authoriseValue.existingDomains,
                    ...(authoriseValue.domains
                      ? authoriseValue.domains.split(',').map((s) => s.trim())
                      : [])
                  ])
                : undefined,
            amount: transactionQuantity,
            expiredAt: getNewDate(datePlusOneYear).endOf('day').toISOString()
          }
        };
        dispatch(purchaseAuthorisationOrderStart(orderData));
      } else {
        const formData = {
          authorisationType,
          domains:
            authoriseValue.mode === 'match-email-domains'
              ? authoriseValue.domains.split(',').map((s) => s.trim())
              : [],
          users:
            authoriseValue.mode === 'add-specific-users'
              ? authoriseValue.emails.split(',').map((s) => s.trim())
              : [],
          orders: [
            {
              amount: transactionQuantity,
              expiredAt: getNewDate(datePlusOneYear).endOf('day').toISOString()
            }
          ]
        };
        dispatch(purchaseAuthorisationStart(formData));
      }
    } else if (activeStep === 3 && postPaymentId) {
      dispatch(confirmPostPaymentStart(postPaymentId, purchaseOrderNumber));
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <TransactionTypeStep
            isLoading={isListAuthorisationsLoading}
            authorisationType={authorisationType}
            onChange={(type) => {
              setAuthorisationType(type);
              setTransactionQuantity(MIN_TRANSACTION_QUANTITY);
            }}
            authorisations={authorisations}
          />
        );
      case 1:
        return (
          <TransactionAmountStep
            isLoading={!!gettingPaymentTypePrice}
            paymentTypePrice={paymentTypePrice || 0}
            priceIncludeTax={priceIncludeTax || false}
            quantity={transactionQuantity}
            onQuantityChange={setTransactionQuantity}
            authorisationType={authorisationType}
          />
        );
      case 2:
        return (
          <AuthoriseStep
            value={authoriseValue}
            onChange={(value) => {
              setAuthoriseValue(value);
              dispatch(clearError());
            }}
            errorMessage={authorisationError}
          />
        );
      case 3:
        return postPaymentId ? (
          <InvoicePayment
            authorisationType={authorisationType}
            quantity={transactionQuantity}
            pricePerTransaction={paymentTypePrice || 0}
            priceIncludeTax={priceIncludeTax || false}
            purchaseOrderNumber={purchaseOrderNumber}
            onPurchaseOrderNumberChange={(value) =>
              setPurchaseOrderNumber(value)
            }
          />
        ) : (
          <StripeEmbeddedCheckout
            paymentType={authorisationType}
            returnPath={APP_URL.PURCHASE_TRANSACTIONS}
            quantity={transactionQuantity}
            metaData={{ authorisationPurchaseId }}
          />
        );
      case 4:
      case 5:
        return (
          <CompletedStep
            authorisationType={authorisationType}
            validUntil={getNewDate(expiredAt).format('MMMM D, YYYY')}
            paymentIntentId={paymentIntentId || ''}
            totalTransactions={transactionQuantity}
            previousBalance={previousBalance}
            newBalance={Number(transactionQuantity) + Number(previousBalance)}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const queryParams: Params = {
      fields: 'id,authorisationType,transactionsLeft,expires',
      include: 'Orders,Users,Domains'
    };
    dispatch(listAuthorisationsStart(queryParams));

    return () => {
      dispatch(clearPaymentState());
      dispatch(clearAllAuthorisations());
    };
  }, [dispatch]);

  useEffect(() => {
    const existingAuthorisation = authorisations?.find(
      (authorisation) => authorisation.authorisationType === authorisationType
    );

    setAuthoriseValue(
      existingAuthorisation
        ? {
            mode:
              existingAuthorisation.users.length > 0
                ? 'add-specific-users'
                : 'match-email-domains',
            existingEmails: existingAuthorisation.users,
            existingDomains: existingAuthorisation.domains,
            emails: '',
            domains: '',
            isExisting: true,
            confirmed: true
          }
        : {
            mode: 'add-specific-users',
            existingEmails: [],
            existingDomains: [],
            emails: '',
            domains: '',
            isExisting: false,
            confirmed: false
          }
    );
    setPreviousBalance(existingAuthorisation?.transactionsLeft || 0);
  }, [authorisations, authorisationType]);

  useEffect(() => {
    if (authorisationProcess === 'listing') {
      listAuthorisationsStartedRef.current = true;
      return;
    }
    if (
      listAuthorisationsStartedRef.current &&
      authorisationProcess === undefined
    ) {
      setIsListAuthorisationsLoading(false);
    }
  }, [authorisationProcess]);

  useEffect(() => {
    if (authorisationPurchaseId) {
      dispatch(
        setPurchasedAuthorisation({
          type: authorisationType,
          newBalance: transactionQuantity,
          previousBalance,
          expiredAt: getNewDate(expiredAt).endOf('day').toISOString()
        })
      );
      setActiveStep(3);
    }
  }, [
    authorisationPurchaseId,
    authorisationType,
    transactionQuantity,
    previousBalance,
    expiredAt,
    dispatch
  ]);

  useEffect(() => {
    if (postPaymentId) {
      setActiveStep(3);
    }
  }, [postPaymentId]);

  useEffect(() => {
    if (postPaymentConfirmed) {
      setActiveStep(5);
    }
  }, [postPaymentConfirmed]);

  useEffect(() => {
    if (checkoutSessionStatus === 'COMPLETED') {
      setActiveStep(5);
    }
  }, [checkoutSessionStatus]);

  useEffect(() => {
    if (location.search) {
      const queryParams = new URLSearchParams(location.search);
      const sessionId = queryParams.get('session_id');
      if (sessionId) {
        queryParams.delete('session_id');
        history.replace({
          search: queryParams.toString()
        });
        dispatch(checkCheckoutSessionStatusStart(sessionId));
        setActiveStep(4);
        setAuthorisationType(
          purchasedAuthorisation?.type || 'WEB_ENQUIRY_AUTOMATION'
        );
        setTransactionQuantity(
          purchasedAuthorisation?.newBalance || MIN_TRANSACTION_QUANTITY
        );
        setPreviousBalance(purchasedAuthorisation?.previousBalance || 0);
        setExpiredAt(
          new Date(
            purchasedAuthorisation?.expiredAt ||
              getNewDate().endOf('day').toISOString()
          )
        );
        dispatch(setPurchasedAuthorisation(undefined));
      }
    }
  }, [location, dispatch, history, purchasedAuthorisation]);

  return (
    <div
      id="purchase-transactions"
      data-testid="purchaseTransactions"
      className="purchase-transactions"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Purchase transactions"
        pageDescription="Buy transaction bundles to use for your enquiries"
        enableHeaderGlobalBar={true}
      >
        <WizardShell
          title="Purchase transactions"
          subtitle="Buy transaction bundles to use for your enquiries"
          steps={steps}
          stepPositionPercents={PURCHASE_TRANSACTION_STEP_POSITIONS}
          currentStepIndex={activeStep}
          onBack={showBack ? goBack : undefined}
          showBack={showBack}
          primaryAction={{
            label: activeStep === 3 && postPaymentId ? 'Confirm' : 'Continue',
            onClick: handlePrimaryActionClick,
            disabled: isContinueDisabled(),
            loading: false,
            show: !hidePrimaryAction
          }}
        >
          {isCheckingCheckoutSessionStatus ||
          confirmingPostPayment ||
          authorisationProcess === 'purchasing' ||
          authorisationOrderProcess === 'purchasing' ? (
            <div className="purchase-transactions--loading-container">
              <Loading withOverlay={false} />
            </div>
          ) : (
            <>
              {checkoutSessionStatus === 'FAILED' || paymentError ? (
                <PaymentUnsuccessfulScreen
                  onTryAgain={() => {
                    setActiveStep(2);
                    dispatch(clearPaymentState());
                    dispatch(getPaymentTypePriceStart(authorisationType));
                  }}
                  onCancel={() => window.location.reload()}
                />
              ) : (
                renderStep()
              )}
            </>
          )}
        </WizardShell>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default PurchaseTransactions;
